import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getJobBySlug, listJobCategories, listJobs } from "../data/cmsStore";
import { fetchPublicCmsData, submitJobApplicationApi } from "../api/cmsApi";

const MAX_RESUME_BYTES = 1.5 * 1024 * 1024;

function lines(text) {
  return String(text || "")
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);
}

export default function CareerDetailPage() {
  const { slug } = useParams();
  const [jobs, setJobs] = useState(() => listJobs());
  const [categories, setCategories] = useState(() => listJobCategories());
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", phone: "", coverLetter: "" });
  const [resumeFile, setResumeFile] = useState(null);
  const [resumeName, setResumeName] = useState("");
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const data = await fetchPublicCmsData();
        if (Array.isArray(data.jobs) && data.jobs.length) setJobs(data.jobs);
        if (Array.isArray(data.jobCategories) && data.jobCategories.length) {
          setCategories(data.jobCategories);
        }
      } catch (_) {
        // Keep local fallback data if API is unreachable.
      } finally {
        setLoading(false);
      }
    })();
  }, []);
  const job = useMemo(
    () => jobs.find((j) => j.slug === slug) || getJobBySlug(slug),
    [jobs, slug]
  );

  if (loading && !job) {
    return (
      <div className="cms-public-page container py-5">
        <h1>Loading job details...</h1>
      </div>
    );
  }

  if (!job || job.status !== "open") {
    return (
      <div className="cms-public-page container py-5">
        <h1>Job not found</h1>
        <Link to="/careers">Back to careers</Link>
      </div>
    );
  }

  const cat = categories.find((c) => c.id === job.categoryId)?.name;
  const about = lines(job.description);
  const responsibilities = lines(job.responsibilities);
  const requirements = lines(job.requirements);
  const benefits = lines(job.benefits);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_RESUME_BYTES) {
      setError("Resume must be under 1.5 MB.");
      setResumeFile(null);
      setResumeName("");
      return;
    }
    setResumeFile(file);
    setResumeName(file.name);
    setError("");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!resumeFile) {
      setError("Please upload your resume.");
      return;
    }
    setError("");
    setSubmitting(true);
    const formData = new FormData();
    formData.append("jobId", job.id);
    formData.append("jobTitle", job.title);
    formData.append("name", form.name);
    formData.append("email", form.email);
    formData.append("phone", form.phone);
    formData.append("coverLetter", form.coverLetter);
    formData.append("route", "careers");
    formData.append("resume", resumeFile, resumeFile.name);
    try {
      await submitJobApplicationApi(formData);
      setSent(true);
    } catch (err) {
      setError(err?.message || "Could not submit application. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="cms-public-page">
      <section className="cms-section animate-on-scroll">
        <div className="container">
          <div className="row g-4 g-lg-5">
            <div className="col-lg-7">
              <Link to="/careers" className="cms-back">← All careers</Link>
              {cat ? <span className="cms-chip">{cat}</span> : null}
              <h1 className="cms-job-title">{job.title}</h1>
              <p className="cms-article-lead mb-4">
                Join Emicron’s {job.department || cat || "delivery"} team and help customers run reliable cloud, security, and managed IT operations.
              </p>

              <div className="cms-info-grid">
                <div className="cms-info-card">
                  <span>Location</span>
                  <strong>{job.location || "—"}</strong>
                </div>
                <div className="cms-info-card">
                  <span>Employment type</span>
                  <strong>{job.type || "—"}</strong>
                </div>
                <div className="cms-info-card">
                  <span>Experience</span>
                  <strong>{job.experience || "—"}</strong>
                </div>
                <div className="cms-info-card">
                  <span>Compensation</span>
                  <strong>{job.salary || "Competitive"}</strong>
                </div>
                <div className="cms-info-card">
                  <span>Department</span>
                  <strong>{job.department || cat || "—"}</strong>
                </div>
                <div className="cms-info-card">
                  <span>Openings</span>
                  <strong>{job.openings || "1"}</strong>
                </div>
                <div className="cms-info-card">
                  <span>Posted</span>
                  <strong>{job.postedAt || "—"}</strong>
                </div>
                <div className="cms-info-card">
                  <span>Status</span>
                  <strong className="cms-status-open">Open</strong>
                </div>
              </div>

              <h3>About the role</h3>
              {about.map((p) => (
                <p key={p.slice(0, 28)}>{p}</p>
              ))}

              {responsibilities.length ? (
                <>
                  <h3>Key responsibilities</h3>
                  <ul className="cms-check-list">
                    {responsibilities.map((r) => (
                      <li key={r}>{r}</li>
                    ))}
                  </ul>
                </>
              ) : null}

              <h3>Requirements</h3>
              <ul className="cms-check-list">
                {requirements.map((r) => (
                  <li key={r}>{r}</li>
                ))}
              </ul>

              {benefits.length ? (
                <>
                  <h3>What we offer</h3>
                  <ul className="cms-check-list cms-check-list--green">
                    {benefits.map((b) => (
                      <li key={b}>{b}</li>
                    ))}
                  </ul>
                </>
              ) : null}
            </div>

            <div className="col-lg-5">
              <div className="cms-apply-card cms-apply-card--sticky">
                <h2>Apply now</h2>
                <p className="cms-apply-note">
                  Share your profile for <strong>{job.title}</strong>. Resume max 1.5 MB (PDF/DOC).
                </p>
                {sent ? (
                  <div className="cms-success">
                    <i className="bi bi-check-circle-fill" />
                    Application submitted. Our HR team will contact you soon.
                  </div>
                ) : (
                  <form onSubmit={onSubmit}>
                    {error ? <div className="cms-error">{error}</div> : null}
                    <label>
                      Full name
                      <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                    </label>
                    <label>
                      Email
                      <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                    </label>
                    <label>
                      Phone
                      <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                    </label>
                    <label>
                      Cover letter
                      <textarea rows={3} value={form.coverLetter} onChange={(e) => setForm({ ...form, coverLetter: e.target.value })} />
                    </label>
                    <label>
                      Resume (PDF/DOC, max 1.5MB)
                      <input type="file" accept=".pdf,.doc,.docx,application/pdf" required onChange={onFile} />
                    </label>
                    {resumeName ? <div className="cms-file-name">{resumeName}</div> : null}
                    <button type="submit" className="cms-btn cms-btn-block" disabled={submitting}>
                      {submitting ? "Submitting..." : "Submit application"}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
